import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import i18n from "@/lib/i18n";

import {
  TASK_STATUSES,
  type Tag,
  type Task,
  type TaskListResponse,
  type TaskStatus,
} from "@/lib/project-types";
import { getTaskChange, TASK_CHANGED_EVENT } from "@/lib/project-events";
import { fetchWithAuth } from "@/lib/utils";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, isProjectLiveConfirmed, readCache, writeCache } from "@/lib/ui-cache";
import { restoreTaskPreview, taskPreview, type CachedTask } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";
import { dueDateSupported, todayInTimezone, type TaskDueFilter } from "@/lib/task-due-date";
import { tagFilterSupported } from "@/lib/task-tags-filter";

const PROJECTS_API_BASE = "/api/projects";
const TASKS_API_BASE = "/api/tasks";

export const KANBAN_PAGE_SIZE = 50;
export const KANBAN_POLL_INTERVAL_MS = 5_000;
const KANBAN_POLL_MAX_BACKOFF_MS = 60_000;

interface KanbanPollBackoffState {
  consecutiveFailures: number;
  retryAt: number;
}

export interface KanbanColumnPageState {
  /** Server pages stay separate so refreshing page one cannot erase page two. */
  pages: Task[][];
  /** Cursor after the oldest loaded server page. */
  nextCursor: string | null;
  hasMore: boolean;
  initialLoading: boolean;
  loadingMore: boolean;
  error: string | null;
  /** New page-one tasks held back while the reader is below the top. */
  pendingNew: Task[];
}

export type KanbanColumns = Record<TaskStatus, KanbanColumnPageState>;

export interface KanbanColumnView {
  tasks: Task[];
  hasMore: boolean;
  initialLoading: boolean;
  loadingMore: boolean;
  error: string | null;
  pendingNewCount: number;
}

export type KanbanColumnViews = Record<TaskStatus, KanbanColumnView>;

export interface KanbanCacheState {
  columns: KanbanColumns;
  /**
   * Local DTOs win over stale pages. They cover successful mutations,
   * optimistic status moves, newly created tasks, and deep-link fetches.
   */
  overlays: Record<string, Task>;
  /** Session-local placement for tasks not ordered by a server-side rank. */
  localOrder: Record<TaskStatus, string[]>;
}

export interface KanbanMoveSnapshot {
  taskId: string;
  sourceStatus: TaskStatus;
  destinationStatus: TaskStatus;
  hadOverlay: boolean;
  previousOverlay?: Task;
  localOrder: Record<TaskStatus, string[]>;
}

type MutationResult = boolean | Task | null | void;
export type BulkTaskAction = "set_status" | "add_tags" | "remove_tags" | "cancel";
export interface BulkTaskRequest {
  task_ids: string[];
  action: BulkTaskAction;
  status?: TaskStatus;
  tag_ids?: string[];
}
export interface BulkTaskResult {
  succeeded: string[];
  failed: { id: string; reason: string }[];
  unsupported?: boolean;
  error?: boolean;
}
export type KanbanTaskFetcher = typeof fetchWithAuth;
export type TaskAssigneeFilter = "all" | "mine";

export interface UseKanbanTasksReturn {
  dueDateSupported: boolean | null;
  tagFilterSupported: boolean | null;
  columns: KanbanColumnViews;
  tasks: Task[];
  initialLoading: boolean;
  initialized: boolean;
  taskLookupLoading: boolean;
  taskLookupError: string | null;
  loadMore: (status: TaskStatus) => Promise<void>;
  retry: (status: TaskStatus) => Promise<void>;
  revealPending: (status: TaskStatus) => void;
  setColumnAtTop: (status: TaskStatus, isAtTop: boolean) => void;
  /** Reload every visible status lane for the active project. */
  refresh: () => Promise<void>;
  upsert: (task: Task) => void;
  moveTask: (
    taskId: string,
    destinationStatus: TaskStatus,
    persist: () => Promise<MutationResult>,
  ) => Promise<boolean>;
  bulkAction: (request: BulkTaskRequest, availableTags?: Tag[]) => Promise<BulkTaskResult>;
  fetchTask: (taskId: string) => Promise<Task | null>;
}

function emptyLocalOrder(): Record<TaskStatus, string[]> {
  return TASK_STATUSES.reduce((order, status) => {
    order[status] = [];
    return order;
  }, {} as Record<TaskStatus, string[]>);
}

function createKanbanPollBackoffState(): Record<TaskStatus, KanbanPollBackoffState> {
  return TASK_STATUSES.reduce((state, status) => {
    state[status] = { consecutiveFailures: 0, retryAt: 0 };
    return state;
  }, {} as Record<TaskStatus, KanbanPollBackoffState>);
}

function nextKanbanPollBackoffMs(consecutiveFailures: number): number {
  return Math.min(
    KANBAN_POLL_INTERVAL_MS * (2 ** consecutiveFailures),
    KANBAN_POLL_MAX_BACKOFF_MS,
  );
}

export function createKanbanColumnPageState(
  initialLoading = false,
): KanbanColumnPageState {
  return {
    pages: [],
    nextCursor: null,
    hasMore: false,
    initialLoading,
    loadingMore: false,
    error: null,
    pendingNew: [],
  };
}

export function createKanbanCacheState(
  initialLoading = false,
): KanbanCacheState {
  const columns = TASK_STATUSES.reduce((state, status) => {
    state[status] = createKanbanColumnPageState(initialLoading);
    return state;
  }, {} as KanbanColumns);

  return {
    columns,
    overlays: {},
    localOrder: emptyLocalOrder(),
  };
}

export function kanbanTasksUrl(
  projectId: string,
  status: TaskStatus,
  assigneeFilter: TaskAssigneeFilter = "all",
  before?: string | null,
  dueFilter: TaskDueFilter = "all",
  timezone = "Europe/Zurich",
  tagIds: readonly string[] = [],
  effectiveTagScope = false,
): string {
  const params = new URLSearchParams();
  params.set("status", status);
  params.set("limit", String(KANBAN_PAGE_SIZE));
  if (assigneeFilter === "mine") params.set("assigned_to", "me");
  if (before) params.set("before", before);
  for (const tagId of tagIds) params.append("tag_ids", tagId);
  if (effectiveTagScope && tagIds.length > 0) params.set("tag_scope", "effective");
  const today = todayInTimezone(timezone);
  if (dueFilter === "overdue") params.set("due_before", today);
  if (dueFilter === "upcoming") params.set("due_on_or_after", today);
  if (dueFilter === "no_date") params.set("due_is_null", "true");
  return `${PROJECTS_API_BASE}/${encodeURIComponent(projectId)}/tasks?${params}`;
}

export function dedupeTasks(tasks: Task[]): Task[] {
  const seen = new Set<string>();
  return tasks.filter((task) => {
    if (seen.has(task.id)) return false;
    seen.add(task.id);
    return true;
  });
}

function assertHierarchyTasks(tasks: Task[], supported: boolean): void {
  if (supported && tasks.some((task) => !Object.prototype.hasOwnProperty.call(task, "parent_task_id")
    || !Array.isArray(task.ancestor_path)
    || !Array.isArray(task.effective_tags))) {
    throw new Error(i18n.t("projects:subtasks.schemaFailed"));
  }
}

function taskMap(cache: KanbanCacheState): Map<string, Task> {
  const tasks = new Map<string, Task>();
  for (const status of TASK_STATUSES) {
    for (const page of cache.columns[status].pages) {
      for (const task of page) {
        if (!tasks.has(task.id)) tasks.set(task.id, task);
      }
    }
    for (const task of cache.columns[status].pendingNew) {
      if (!tasks.has(task.id)) tasks.set(task.id, task);
    }
  }
  for (const task of Object.values(cache.overlays)) tasks.set(task.id, task);
  return tasks;
}

export function findKanbanTask(
  cache: KanbanCacheState,
  taskId: string,
): Task | null {
  return taskMap(cache).get(taskId) ?? null;
}

/**
 * `updated_at` is the task API's change version. Poll responses are freshly
 * decoded objects, so compare versions rather than object identity before
 * deciding whether a response needs to replace cached UI state.
 */
function sameTaskVersion(left: Task, right: Task): boolean {
  return left.id === right.id
    && left.status === right.status
    && left.updated_at === right.updated_at;
}

function replaceKnownTasks(pages: Task[][], incoming: Map<string, Task>): Task[][] {
  let changed = false;
  const nextPages = pages.map((page) => {
    let pageChanged = false;
    const nextPage = page.map((task) => {
      const replacement = incoming.get(task.id);
      if (!replacement || sameTaskVersion(task, replacement)) return task;
      changed = true;
      pageChanged = true;
      return replacement;
    });
    return pageChanged ? nextPage : page;
  });
  return changed ? nextPages : pages;
}

function sameTaskVersions(left: Task[], right: Task[]): boolean {
  return left.length === right.length
    && left.every((task, index) => sameTaskVersion(task, right[index]!));
}

function sameTaskPages(left: Task[][], right: Task[][]): boolean {
  return left.length === right.length
    && left.every((page, index) => sameTaskVersions(page, right[index]!));
}

function mergePendingTasks(current: Task[], incoming: Task[]): Task[] {
  const incomingById = new Map(incoming.map((task) => [task.id, task]));
  let changed = false;
  const updatedCurrent = current.map((task) => {
    const replacement = incomingById.get(task.id);
    if (!replacement || sameTaskVersion(task, replacement)) return task;
    changed = true;
    return replacement;
  });
  const currentIds = new Set(current.map((task) => task.id));
  const newTasks = incoming.filter((task) => !currentIds.has(task.id));
  if (!changed && newTasks.length === 0) return current;
  return dedupeTasks([...newTasks, ...updatedCurrent]);
}

/**
 * Reconcile a polled first page without changing the cursor owned by the
 * oldest loaded page or deleting anything that may simply have crossed a
 * pagination boundary.
 */
export function reconcileKanbanFirstPage(
  column: KanbanColumnPageState,
  incomingTasks: Task[],
  options: {
    isAtTop: boolean;
    knownTaskIds?: ReadonlySet<string>;
  },
): KanbanColumnPageState {
  const incoming = dedupeTasks(incomingTasks);
  const incomingById = new Map(incoming.map((task) => [task.id, task]));
  const pages = replaceKnownTasks(column.pages, incomingById);
  const loadedIds = new Set(
    pages.flatMap((page) => page.map((task) => task.id)),
  );
  const pendingIds = new Set(column.pendingNew.map((task) => task.id));
  for (const task of column.pendingNew) loadedIds.add(task.id);
  for (const id of options.knownTaskIds ?? []) loadedIds.add(id);

  const newlyDiscovered = incoming.filter((task) => !loadedIds.has(task.id));
  const pendingUpdates = incoming.filter((task) => pendingIds.has(task.id));

  if (options.isAtTop) {
    const knownById = new Map(
      pages.flatMap((page) => page.map((task) => [task.id, task])),
    );
    const firstPage = dedupeTasks([
      ...incoming.filter(
        (task) => !column.pendingNew.some((pending) => pending.id === task.id),
      ).map((task) => knownById.get(task.id) ?? task),
      ...(pages[0] ?? []),
    ]);
    const next = {
      ...column,
      pages: pages.length > 0 ? [firstPage, ...pages.slice(1)] : [firstPage],
      pendingNew: mergePendingTasks(column.pendingNew, pendingUpdates),
      error: null,
    };
    return sameTaskPages(column.pages, next.pages)
      && sameTaskVersions(column.pendingNew, next.pendingNew)
      && column.error === next.error
      ? column
      : next;
  }

  const next = {
    ...column,
    pages,
    pendingNew: mergePendingTasks(
      column.pendingNew,
      [...newlyDiscovered, ...pendingUpdates],
    ),
    error: null,
  };
  return sameTaskPages(column.pages, next.pages)
    && sameTaskVersions(column.pendingNew, next.pendingNew)
    && column.error === next.error
    ? column
    : next;
}

/** Append one cursor page, replacing overlaps by ID and detecting no progress. */
export function appendKanbanPage(
  column: KanbanColumnPageState,
  response: TaskListResponse,
): KanbanColumnPageState {
  const incoming = dedupeTasks(response.tasks);
  const incomingById = new Map(incoming.map((task) => [task.id, task]));
  const pages = replaceKnownTasks(column.pages, incomingById);
  const existingIds = new Set(
    pages.flatMap((page) => page.map((task) => task.id)),
  );
  const uniqueTasks = incoming.filter((task) => !existingIds.has(task.id));
  const madeProgress = uniqueTasks.length > 0 || incoming.length === 0;

  return {
    ...column,
    pages: uniqueTasks.length > 0 ? [...pages, uniqueTasks] : pages,
    nextCursor: response.next_cursor,
    hasMore: response.has_more,
    loadingMore: false,
    error: madeProgress ? null : i18n.t("projects:noAdditionalTasks"),
  };
}

function cloneLocalOrder(
  localOrder: Record<TaskStatus, string[]>,
): Record<TaskStatus, string[]> {
  return TASK_STATUSES.reduce((copy, status) => {
    copy[status] = [...localOrder[status]];
    return copy;
  }, {} as Record<TaskStatus, string[]>);
}

export function upsertKanbanTask(
  cache: KanbanCacheState,
  task: Task,
  options: { pinToTop?: boolean } = {},
): KanbanCacheState {
  const existing = findKanbanTask(cache, task.id);
  const localOrder = cloneLocalOrder(cache.localOrder);
  const existingLocalIndex = existing
    ? localOrder[existing.status].indexOf(task.id)
    : -1;

  if (!existing || existing.status !== task.status || options.pinToTop) {
    for (const status of TASK_STATUSES) {
      localOrder[status] = localOrder[status].filter((id) => id !== task.id);
    }
    localOrder[task.status] = [task.id, ...localOrder[task.status]];
  } else if (existingLocalIndex >= 0) {
    localOrder[task.status][existingLocalIndex] = task.id;
  }

  const columns = TASK_STATUSES.reduce((next, status) => {
    next[status] = {
      ...cache.columns[status],
      pendingNew: cache.columns[status].pendingNew.filter(
        (pending) => pending.id !== task.id,
      ),
    };
    return next;
  }, {} as KanbanColumns);

  return {
    columns,
    overlays: {
      ...cache.overlays,
      [task.id]: task,
    },
    localOrder,
  };
}

export function optimisticallyMoveKanbanTask(
  cache: KanbanCacheState,
  taskId: string,
  destinationStatus: TaskStatus,
): { cache: KanbanCacheState; snapshot: KanbanMoveSnapshot } | null {
  const task = findKanbanTask(cache, taskId);
  if (!task || task.status === destinationStatus) return null;

  const snapshot: KanbanMoveSnapshot = {
    taskId,
    sourceStatus: task.status,
    destinationStatus,
    hadOverlay: Object.prototype.hasOwnProperty.call(cache.overlays, taskId),
    previousOverlay: cache.overlays[taskId],
    localOrder: cloneLocalOrder(cache.localOrder),
  };
  const optimisticTask: Task = {
    ...task,
    status: destinationStatus,
  };

  return {
    cache: upsertKanbanTask(cache, optimisticTask, { pinToTop: true }),
    snapshot,
  };
}

export function rollbackKanbanTaskMove(
  cache: KanbanCacheState,
  snapshot: KanbanMoveSnapshot,
): KanbanCacheState {
  const overlays = { ...cache.overlays };
  if (snapshot.hadOverlay && snapshot.previousOverlay) {
    overlays[snapshot.taskId] = snapshot.previousOverlay;
  } else {
    delete overlays[snapshot.taskId];
  }

  return {
    ...cache,
    overlays,
    localOrder: cloneLocalOrder(snapshot.localOrder),
  };
}

/** Restore one failed member of a batch while retaining successful members. */
export function rollbackBulkKanbanTask(
  current: KanbanCacheState,
  before: KanbanCacheState,
  taskId: string,
): KanbanCacheState {
  const original = findKanbanTask(before, taskId);
  if (!original) return current;
  const overlays = { ...current.overlays, [taskId]: original };
  const localOrder = cloneLocalOrder(current.localOrder);
  for (const status of TASK_STATUSES) {
    localOrder[status] = localOrder[status].filter((id) => id !== taskId);
  }
  const priorIndex = before.localOrder[original.status].indexOf(taskId);
  if (priorIndex >= 0) {
    localOrder[original.status].splice(priorIndex, 0, taskId);
  }
  return { ...current, overlays, localOrder };
}

function compareTaskFreshness(incoming: Task, current: Task): number {
  const incomingTimestamp = Date.parse(incoming.updated_at);
  const currentTimestamp = Date.parse(current.updated_at);
  if (
    Number.isFinite(incomingTimestamp)
    && Number.isFinite(currentTimestamp)
  ) {
    return incomingTimestamp - currentTimestamp;
  }
  return incoming.updated_at.localeCompare(current.updated_at);
}

/**
 * Replace a local DTO only with an equal-or-newer matching server DTO, or with
 * a strictly newer server-side status change. Keeping that normalized DTO
 * prevents an older in-flight source-column poll from reviving a moved card;
 * localOrder preserves the session-local placement.
 */
export function reconcileKanbanServerTasks(
  cache: KanbanCacheState,
  incomingTasks: Task[],
  pendingTaskIds: ReadonlySet<string> = new Set(),
): KanbanCacheState {
  const overlays = { ...cache.overlays };
  const confirmed = new Map<string, TaskStatus>();
  const localOrder = cloneLocalOrder(cache.localOrder);

  for (const incoming of incomingTasks) {
    if (pendingTaskIds.has(incoming.id)) continue;
    const overlay = overlays[incoming.id];
    const currentTask = overlay ?? findKanbanTask(cache, incoming.id);
    if (!currentTask) continue;
    if (!overlay && currentTask.status === incoming.status) continue;

    const freshness = compareTaskFreshness(incoming, currentTask);
    const sameStatus = currentTask.status === incoming.status;
    if ((sameStatus && freshness < 0) || (!sameStatus && freshness <= 0)) {
      continue;
    }

    overlays[incoming.id] = incoming;
    confirmed.set(incoming.id, incoming.status);
    if (!sameStatus) {
      for (const status of TASK_STATUSES) {
        localOrder[status] = localOrder[status].filter(
          (taskId) => taskId !== incoming.id,
        );
      }
      localOrder[incoming.status] = [
        incoming.id,
        ...localOrder[incoming.status],
      ];
    }
  }

  if (confirmed.size === 0) return cache;

  const columns = TASK_STATUSES.reduce((next, status) => {
    const removeIds = new Set(
      [...confirmed]
        .filter(([, confirmedStatus]) => confirmedStatus !== status)
        .map(([taskId]) => taskId),
    );
    const column = cache.columns[status];
    next[status] = removeIds.size === 0
      ? column
      : {
        ...column,
        pages: column.pages.map((page) => (
          page.filter((task) => !removeIds.has(task.id))
        )),
        pendingNew: column.pendingNew.filter(
          (task) => !removeIds.has(task.id),
        ),
      };
    return next;
  }, {} as KanbanColumns);

  return {
    ...cache,
    columns,
    overlays,
    localOrder,
  };
}

export function revealPendingKanbanTasks(
  cache: KanbanCacheState,
  status: TaskStatus,
): KanbanCacheState {
  const column = cache.columns[status];
  if (column.pendingNew.length === 0) return cache;
  const firstPage = dedupeTasks([
    ...column.pendingNew,
    ...(column.pages[0] ?? []),
  ]);

  return {
    ...cache,
    columns: {
      ...cache.columns,
      [status]: {
        ...column,
        pages: column.pages.length > 0
          ? [firstPage, ...column.pages.slice(1)]
          : [firstPage],
        pendingNew: [],
      },
    },
  };
}

export function deriveKanbanColumnViews(
  cache: KanbanCacheState,
): KanbanColumnViews {
  const views = {} as KanbanColumnViews;
  const emitted = new Set<string>();

  for (const status of TASK_STATUSES) {
    const tasks: Task[] = [];
    const addTask = (task: Task | undefined) => {
      if (!task || task.status !== status || emitted.has(task.id)) return;
      emitted.add(task.id);
      tasks.push(task);
    };

    for (const taskId of cache.localOrder[status]) {
      addTask(cache.overlays[taskId]);
    }
    for (const page of cache.columns[status].pages) {
      for (const serverTask of page) {
        addTask(cache.overlays[serverTask.id] ?? serverTask);
      }
    }
    for (const overlay of Object.values(cache.overlays)) addTask(overlay);

    const column = cache.columns[status];
    views[status] = {
      tasks,
      hasMore: column.hasMore,
      initialLoading: column.initialLoading,
      loadingMore: column.loadingMore,
      error: column.error,
      pendingNewCount: column.pendingNew.length,
    };
  }

  return views;
}

export function staticKanbanColumnViews(tasks: Task[]): KanbanColumnViews {
  const cache = createKanbanCacheState();
  for (const status of TASK_STATUSES) {
    cache.columns[status].pages = [[
      ...tasks.filter((task) => task.status === status),
    ]];
  }
  return deriveKanbanColumnViews(cache);
}

function requestError(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Independently paginated and polled task cache used only by the Kanban board. */
export function useKanbanTasks(
  getToken: () => Promise<string>,
  projectId: string | null,
  assigneeFilter: TaskAssigneeFilter = "all",
  fetcher: KanbanTaskFetcher = fetchWithAuth,
  dueFilter: TaskDueFilter = "all",
  timezone = "Europe/Zurich",
  tagIds: readonly string[] = [],
  effectiveTagScope = false,
): UseKanbanTasksReturn {
  const verifiedScope = useCacheScope();
  const validatedColumnsRef = useRef(new Set<TaskStatus>());
  const [cache, setCache] = useState<KanbanCacheState>(() => (
    createKanbanCacheState(Boolean(projectId))
  ));
  const [taskLookupLoading, setTaskLookupLoading] = useState(false);
  const [taskLookupError, setTaskLookupError] = useState<string | null>(null);
  const [dateSupport, setDateSupport] = useState<boolean | null>(null);
  const [tagSupport, setTagSupport] = useState<boolean | null>(null);
  const tagSupportProjectRef = useRef<string | null>(null);
  const tagIdsKey = [...tagIds].sort().join(",");
  const tagIdsRef = useRef(tagIds);
  tagIdsRef.current = tagIds;
  const effectiveTagScopeRef = useRef(effectiveTagScope);
  effectiveTagScopeRef.current = effectiveTagScope;
  const tagSupportRef = useRef(tagSupport);
  tagSupportRef.current = tagSupport;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const assigneeFilterRef = useRef(assigneeFilter);
  assigneeFilterRef.current = assigneeFilter;
  const dueFilterRef = useRef(dueFilter);
  dueFilterRef.current = dueFilter;
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;
  const cacheRef = useRef(cache);
  const generationRef = useRef(0);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const columnAtTopRef = useRef<Record<TaskStatus, boolean>>(
    TASK_STATUSES.reduce((state, status) => {
      state[status] = true;
      return state;
    }, {} as Record<TaskStatus, boolean>),
  );
  const mutationSequenceRef = useRef(0);
  const pendingMutationRef = useRef<Map<string, number>>(new Map());
  const pollBackoffRef = useRef<Record<TaskStatus, KanbanPollBackoffState>>(
    createKanbanPollBackoffState(),
  );

  const updateCache = useCallback((
    updater: (current: KanbanCacheState) => KanbanCacheState,
  ) => {
    setCache((current) => {
      const next = updater(current);
      cacheRef.current = next;
      return next;
    });
  }, []);

  const abortAll = useCallback(() => {
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current.clear();
  }, []);

  const startRequest = useCallback((key: string): AbortController => {
    controllersRef.current.get(key)?.abort();
    const controller = new AbortController();
    controllersRef.current.set(key, controller);
    return controller;
  }, []);

  const finishRequest = useCallback((
    key: string,
    controller: AbortController,
  ) => {
    if (controllersRef.current.get(key) === controller) {
      controllersRef.current.delete(key);
    }
  }, []);

  const clearPollBackoff = useCallback(() => {
    for (const status of TASK_STATUSES) {
      pollBackoffRef.current[status] = {
        consecutiveFailures: 0,
        retryAt: 0,
      };
    }
  }, []);

  const fetchInitialPage = useCallback(async (
    requestedProjectId: string,
    status: TaskStatus,
    generation: number,
  ): Promise<void> => {
    const key = `column:${status}`;
    const controller = startRequest(key);

    updateCache((current) => ({
      ...current,
      columns: {
        ...current.columns,
        [status]: {
          ...current.columns[status],
          initialLoading: true,
          error: null,
        },
      },
    }));

    try {
      const response = await fetcherRef.current(
        kanbanTasksUrl(requestedProjectId, status, assigneeFilterRef.current, null, dueFilterRef.current, timezoneRef.current, tagSupportRef.current === true && tagSupportProjectRef.current === requestedProjectId ? tagIdsRef.current : [], effectiveTagScopeRef.current),
        { headers: {}, signal: controller.signal },
        getTokenRef.current,
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          clearCacheOnAuthFailure(); void invalidateCache("taskPages");
          updateCache(() => createKanbanCacheState(Boolean(requestedProjectId)));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data: TaskListResponse = await response.json();
      assertHierarchyTasks(data.tasks, effectiveTagScopeRef.current);
      if (controller.signal.aborted || generationRef.current !== generation) return;
      tagSupportProjectRef.current = requestedProjectId;
      setDateSupport((current) => current === false ? false : dueDateSupported(data));
      setTagSupport((current) => current === false ? false : tagFilterSupported(data));
      const tasks = dedupeTasks(data.tasks.filter(
        (task) => task.project_id === requestedProjectId && task.status === status,
      ));
      validatedColumnsRef.current.add(status);
      if (assigneeFilterRef.current === "all" && dueFilterRef.current === "all" && tagIdsRef.current.length === 0) void writeCache("taskPages", "status", `${requestedProjectId}:status:${status}`, { tasks: tasks.map(taskPreview).filter((row): row is CachedTask => !!row), has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });
      const pendingTaskIds = new Set(pendingMutationRef.current.keys());

      updateCache((current) => {
        const reconciled = reconcileKanbanServerTasks(
          current,
          tasks,
          pendingTaskIds,
        );
        return {
          ...reconciled,
          columns: {
            ...reconciled.columns,
            [status]: {
              ...reconciled.columns[status],
              pages: [tasks],
              nextCursor: data.next_cursor,
              hasMore: data.has_more,
              initialLoading: false,
              loadingMore: false,
              error: null,
            },
          },
        };
      });
    } catch (error: unknown) {
      if (controller.signal.aborted || generationRef.current !== generation) return;
      updateCache((current) => ({
        ...current,
        columns: {
          ...current.columns,
          [status]: {
            ...current.columns[status],
            initialLoading: false,
            loadingMore: false,
            error: requestError(error, i18n.t("projects:taskLoadFailed")),
          },
        },
      }));
    } finally {
      finishRequest(key, controller);
    }
  }, [finishRequest, startRequest, updateCache]);

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    abortAll();
    pendingMutationRef.current.clear();
    validatedColumnsRef.current.clear();
    clearPollBackoff();
    setTaskLookupLoading(false);
    setTaskLookupError(null);
    setDateSupport(null);
    if (tagSupportProjectRef.current !== projectId) setTagSupport(null);
    for (const status of TASK_STATUSES) columnAtTopRef.current[status] = true;

    const next = createKanbanCacheState(Boolean(projectId));
    cacheRef.current = next;
    setCache(next);

    if (!projectId) return;
    let cancelled = false;
    if (verifiedScope && assigneeFilter === "all" && dueFilter === "all" && tagIds.length === 0) for (const status of TASK_STATUSES) {
      void readCache<{ tasks: CachedTask[]; has_more?: boolean; next_cursor?: string | null }>("taskPages", "status", `${projectId}:status:${status}`).then((snapshot) => {
        if (cancelled || cacheScope() !== verifiedScope || !isProjectLiveConfirmed(projectId) || validatedColumnsRef.current.has(status) || !Array.isArray(snapshot?.tasks)) return;
        const tasks = snapshot.tasks.map(restoreTaskPreview).filter((row): row is Task => !!row && row.project_id === projectId && row.status === status);
        if (!tasks.length) return;
        updateCache((current) => ({ ...current, columns: { ...current.columns, [status]: { ...current.columns[status], pages: [tasks], hasMore: snapshot.has_more === true, nextCursor: snapshot.next_cursor ?? null } } }));
      });
    }
    for (const status of TASK_STATUSES) {
      void fetchInitialPage(projectId, status, generation);
    }

    return () => { cancelled = true; abortAll(); };
  }, [abortAll, assigneeFilter, dueFilter, tagIdsKey, effectiveTagScope, timezone, clearPollBackoff, fetchInitialPage, projectId, verifiedScope, updateCache]);

  const loadMore = useCallback(async (status: TaskStatus): Promise<void> => {
    if (!projectId) return;
    const column = cacheRef.current.columns[status];
    if (
      column.initialLoading
      || column.loadingMore
      || !column.hasMore
      || !column.nextCursor
    ) return;

    const generation = generationRef.current;
    const cursor = column.nextCursor;
    const key = `column:${status}`;
    const controller = startRequest(key);
    updateCache((current) => ({
      ...current,
      columns: {
        ...current.columns,
        [status]: {
          ...current.columns[status],
          loadingMore: true,
          error: null,
        },
      },
    }));

    try {
      const response = await fetcherRef.current(
        kanbanTasksUrl(projectId, status, assigneeFilterRef.current, cursor, dueFilterRef.current, timezoneRef.current, tagSupportRef.current === true && tagSupportProjectRef.current === projectId ? tagIdsRef.current : [], effectiveTagScopeRef.current),
        { headers: {}, signal: controller.signal },
        getTokenRef.current,
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          clearCacheOnAuthFailure(); void invalidateCache("taskPages");
          updateCache(() => createKanbanCacheState(Boolean(projectId)));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data: TaskListResponse = await response.json();
      assertHierarchyTasks(data.tasks, effectiveTagScopeRef.current);
      if (controller.signal.aborted || generationRef.current !== generation) return;
      tagSupportProjectRef.current = projectId;
      if (!dueDateSupported(data)) setDateSupport(false);
      if (!tagFilterSupported(data)) setTagSupport(false);
      const filteredData: TaskListResponse = {
        ...data,
        tasks: data.tasks.filter(
          (task) => task.project_id === projectId && task.status === status,
        ),
      };
      const pendingTaskIds = new Set(pendingMutationRef.current.keys());
      updateCache((current) => {
        const reconciled = reconcileKanbanServerTasks(
          current,
          filteredData.tasks,
          pendingTaskIds,
        );
        return {
          ...reconciled,
          columns: {
            ...reconciled.columns,
            [status]: appendKanbanPage(
              reconciled.columns[status],
              filteredData,
            ),
          },
        };
      });
    } catch (error: unknown) {
      if (controller.signal.aborted || generationRef.current !== generation) return;
      updateCache((current) => ({
        ...current,
        columns: {
          ...current.columns,
          [status]: {
            ...current.columns[status],
            loadingMore: false,
            error: requestError(error, i18n.t("projects:taskLoadMoreFailed")),
          },
        },
      }));
    } finally {
      finishRequest(key, controller);
    }
  }, [
    finishRequest,
    projectId,
    startRequest,
    updateCache,
  ]);

  const retry = useCallback(async (status: TaskStatus): Promise<void> => {
    if (!projectId) return;
    const column = cacheRef.current.columns[status];
    if (column.pages.length === 0 || !validatedColumnsRef.current.has(status)) {
      await fetchInitialPage(projectId, status, generationRef.current);
      return;
    }
    await loadMore(status);
  }, [fetchInitialPage, loadMore, projectId]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!projectId) return;
    const generation = generationRef.current;
    await Promise.all(TASK_STATUSES.map((status) => (
      fetchInitialPage(projectId, status, generation)
    )));
  }, [fetchInitialPage, projectId]);

  useRefreshOnReconnect(refresh, Boolean(projectId));

  const pollFirstPage = useCallback(async (
    status: TaskStatus,
    force = false,
  ): Promise<void> => {
    if (!projectId || cacheRef.current.columns[status].pages.length === 0) return;
    const generation = generationRef.current;
    const key = `column:${status}`;
    const url = kanbanTasksUrl(projectId, status, assigneeFilterRef.current, null, dueFilterRef.current, timezoneRef.current, tagSupportRef.current === true && tagSupportProjectRef.current === projectId ? tagIdsRef.current : [], effectiveTagScopeRef.current);
    const backoff = pollBackoffRef.current[status];
    if (!force && Date.now() < backoff.retryAt) return;
    if (!force && controllersRef.current.has(key)) return;
    const controller = startRequest(key);

    try {
      const response = await fetcherRef.current(
        url,
        { headers: {}, signal: controller.signal },
        getTokenRef.current,
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          clearCacheOnAuthFailure(); void invalidateCache("taskPages");
          updateCache(() => createKanbanCacheState(Boolean(projectId)));
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data: TaskListResponse = await response.json();
      assertHierarchyTasks(data.tasks, effectiveTagScopeRef.current);
      if (controller.signal.aborted || generationRef.current !== generation) return;
      tagSupportProjectRef.current = projectId;

      backoff.consecutiveFailures = 0;
      backoff.retryAt = 0;
      setDateSupport((current) => current === false ? false : dueDateSupported(data));
      setTagSupport((current) => current === false ? false : tagFilterSupported(data));
      if (assigneeFilterRef.current === "all" && dueFilterRef.current === "all" && tagIdsRef.current.length === 0) void writeCache("taskPages", "status", `${projectId}:status:${status}`, { tasks: data.tasks.slice(0, 50).map(taskPreview).filter((row): row is CachedTask => !!row), has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });

      updateCache((current) => {
        const knownTaskIds = new Set(taskMap(current).keys());
        const incoming = data.tasks.filter(
          (task) => task.project_id === projectId && task.status === status,
        );
        const reconciled = reconcileKanbanServerTasks(
          current,
          incoming,
          new Set(pendingMutationRef.current.keys()),
        );
        const nextColumn = reconcileKanbanFirstPage(
          reconciled.columns[status],
          incoming,
          {
            isAtTop: columnAtTopRef.current[status],
            knownTaskIds,
          },
        );
        // Do not replace the board's cache for a version-equivalent poll
        // response. Besides avoiding needless renders, this leaves the shared
        // horizontal scroller entirely untouched.
        if (
          reconciled === current
          && nextColumn === current.columns[status]
        ) return current;
        return {
          ...reconciled,
          columns: {
            ...reconciled.columns,
            [status]: nextColumn,
          },
        };
      });
    } catch (error: unknown) {
      if (controller.signal.aborted || generationRef.current !== generation) return;
      backoff.consecutiveFailures += 1;
      const retryInMs = nextKanbanPollBackoffMs(backoff.consecutiveFailures);
      backoff.retryAt = Date.now() + retryInMs;
      console.warn("[kanban-tasks] poll failed", {
        method: "GET",
        url,
        error,
        retryInMs,
      });
    } finally {
      finishRequest(key, controller);
    }
  }, [
    finishRequest,
    projectId,
    startRequest,
    updateCache,
  ]);

  useEffect(() => {
    if (!projectId) return;
    const pollVisibleColumns = (force = false) => {
      if (
        typeof document !== "undefined"
        && document.visibilityState !== "visible"
      ) return;
      for (const status of TASK_STATUSES) void pollFirstPage(status, force);
    };
    const interval = setInterval(pollVisibleColumns, KANBAN_POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      clearPollBackoff();
      // Background tabs can throttle intervals indefinitely. Force a fresh
      // request on return so an old in-flight poll cannot keep the board stale.
      pollVisibleColumns(true);
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      clearInterval(interval);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      for (const status of TASK_STATUSES) {
        controllersRef.current.get(`column:${status}`)?.abort();
      }
    };
  }, [clearPollBackoff, pollFirstPage, projectId]);

  const revealPending = useCallback((status: TaskStatus) => {
    updateCache((current) => revealPendingKanbanTasks(current, status));
  }, [updateCache]);

  const setColumnAtTop = useCallback((
    status: TaskStatus,
    isAtTop: boolean,
  ) => {
    columnAtTopRef.current[status] = isAtTop;
  }, []);

  const upsert = useCallback((task: Task) => {
    if (!projectId || task.project_id !== projectId) return;
    if (!Object.prototype.hasOwnProperty.call(task, "due_date")) setDateSupport(false);
    void invalidateCache("taskPages");
    updateCache((current) => upsertKanbanTask(current, task));
  }, [projectId, updateCache]);

  // A task can be created from an existing conversation while this board is
  // open. Reconcile only its server-filtered status lane rather than replacing
  // every lane and guessing whether it matches the current assignee filter.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleTaskChanged = (event: Event) => {
      const task = getTaskChange(event);
      if (!task || task.project_id !== projectId) return;
      void invalidateCache("taskPages");
      void pollFirstPage(task.status);
    };

    window.addEventListener(TASK_CHANGED_EVENT, handleTaskChanged);
    return () => window.removeEventListener(TASK_CHANGED_EVENT, handleTaskChanged);
  }, [pollFirstPage, projectId]);

  const moveTask = useCallback(async (
    taskId: string,
    destinationStatus: TaskStatus,
    persist: () => Promise<MutationResult>,
  ): Promise<boolean> => {
    const source = findKanbanTask(cacheRef.current, taskId);
    if (!source || !validatedColumnsRef.current.has(source.status)
      || !validatedColumnsRef.current.has(destinationStatus)) return false;
    const optimistic = optimisticallyMoveKanbanTask(
      cacheRef.current,
      taskId,
      destinationStatus,
    );
    if (!optimistic) {
      const task = findKanbanTask(cacheRef.current, taskId);
      return task?.status === destinationStatus;
    }

    mutationSequenceRef.current += 1;
    const mutationId = mutationSequenceRef.current;
    pendingMutationRef.current.set(taskId, mutationId);
    updateCache(() => optimistic.cache);

    try {
      const result = await persist();
      if (pendingMutationRef.current.get(taskId) !== mutationId) return true;
      if (result === false || result === null) {
        updateCache((current) => rollbackKanbanTaskMove(
          current,
          optimistic.snapshot,
        ));
        return false;
      }
      if (typeof result === "object") {
        updateCache((current) => upsertKanbanTask(current, result));
      }
      void invalidateCache("taskPages");
      return true;
    } catch {
      if (pendingMutationRef.current.get(taskId) === mutationId) {
        updateCache((current) => rollbackKanbanTaskMove(
          current,
          optimistic.snapshot,
        ));
      }
      return false;
    } finally {
      if (pendingMutationRef.current.get(taskId) === mutationId) {
        pendingMutationRef.current.delete(taskId);
      }
    }
  }, [updateCache]);

  const bulkAction = useCallback(async (
    request: BulkTaskRequest,
    availableTags: Tag[] = [],
  ): Promise<BulkTaskResult> => {
    const ids = [...new Set(request.task_ids)];
    const failed = ids.filter((id) => (
      !findKanbanTask(cacheRef.current, id) || pendingMutationRef.current.has(id)
    )).map((id) => ({ id, reason: "unavailable" }));
    const eligible = ids.filter((id) => !failed.some((item) => item.id === id));
    if (!projectId || ids.length > 100 || eligible.length === 0) {
      return { succeeded: [], failed: ids.map((id) => ({ id, reason: "unavailable" })), error: true };
    }
    const status = request.action === "cancel" ? "cancelled" : request.status;
    if (status && eligible.every((id) => findKanbanTask(cacheRef.current, id)?.status === status)) {
      return { succeeded: eligible, failed };
    }

    const before = cacheRef.current;
    const generation = generationRef.current;
    const mutationId = ++mutationSequenceRef.current;
    for (const id of eligible) pendingMutationRef.current.set(id, mutationId);
    updateCache((current) => {
      let next = current;
      // upsert pins each moved card to the lane's front. Reverse traversal
      // leaves the final group in its visible source order.
      for (const id of [...eligible].reverse()) {
        const task = findKanbanTask(next, id);
        if (!task) continue;
        if (status) {
          if (task.status !== status) next = upsertKanbanTask(next, { ...task, status }, { pinToTop: true });
        } else if (request.action === "add_tags") {
          const additions = availableTags.filter((tag) => request.tag_ids?.includes(tag.id));
          const tags = [...(task.tags ?? [])];
          for (const tag of additions) if (!tags.some((currentTag) => currentTag.id === tag.id)) tags.push(tag);
          next = upsertKanbanTask(next, { ...task, tags });
        } else if (request.action === "remove_tags") {
          next = upsertKanbanTask(next, {
            ...task, tags: (task.tags ?? []).filter((tag) => !request.tag_ids?.includes(tag.id)),
          });
        }
      }
      return next;
    });

    let result: BulkTaskResult;
    try {
      const response = await fetcherRef.current(
        `${PROJECTS_API_BASE}/${encodeURIComponent(projectId)}/tasks/bulk`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...request, task_ids: eligible }),
        },
        getTokenRef.current,
      );
      if (response.status === 404 || response.status === 405) {
        result = { succeeded: [], failed: eligible.map((id) => ({ id, reason: "unsupported" })), unsupported: true };
      } else if (!response.ok) {
        result = { succeeded: [], failed: eligible.map((id) => ({ id, reason: "request_failed" })), error: true };
      } else {
        const data = await response.json() as BulkTaskResult;
        const successSet = new Set(data.succeeded ?? []);
        result = {
          succeeded: eligible.filter((id) => successSet.has(id)),
          failed: eligible.filter((id) => !successSet.has(id)).map((id) =>
            data.failed?.find((item) => item.id === id) ?? { id, reason: "request_failed" }),
        };
      }
    } catch {
      result = { succeeded: [], failed: eligible.map((id) => ({ id, reason: "request_failed" })), error: true };
    }
    if (generation === generationRef.current) {
      const failedSet = new Set(result.failed.map((item) => item.id));
      updateCache((current) => eligible.reduce((next, id) => (
        failedSet.has(id) ? rollbackBulkKanbanTask(next, before, id) : next
      ), current));
      for (const id of eligible) {
        if (pendingMutationRef.current.get(id) === mutationId) pendingMutationRef.current.delete(id);
      }
      if (result.succeeded.length > 0) void invalidateCache("taskPages");
    }
    return { ...result, failed: [...failed, ...result.failed] };
  }, [projectId, updateCache]);

  const fetchTask = useCallback(async (taskId: string): Promise<Task | null> => {
    if (!projectId) return null;
    const generation = generationRef.current;
    const key = "task-lookup";
    const controller = startRequest(key);
    setTaskLookupLoading(true);
    setTaskLookupError(null);

    try {
      const response = await fetcherRef.current(
        `${TASKS_API_BASE}/${encodeURIComponent(taskId)}`,
        { headers: {}, signal: controller.signal },
        getTokenRef.current,
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const task: Task = await response.json();
      assertHierarchyTasks([task], effectiveTagScopeRef.current);
      if (controller.signal.aborted || generationRef.current !== generation) {
        return null;
      }
      if (task.project_id !== projectId) {
        setTaskLookupError(i18n.t("projects:taskLookupWrongProject"));
        return null;
      }
      if (!Object.prototype.hasOwnProperty.call(task, "due_date")) setDateSupport(false);
      updateCache((current) => upsertKanbanTask(current, task, {
        pinToTop: true,
      }));
      return task;
    } catch (error: unknown) {
      if (!controller.signal.aborted && generationRef.current === generation) {
        setTaskLookupError(requestError(error, i18n.t("projects:taskLookupFailed")));
      }
      return null;
    } finally {
      finishRequest(key, controller);
      if (!controller.signal.aborted && generationRef.current === generation) {
        setTaskLookupLoading(false);
      }
    }
  }, [
    finishRequest,
    projectId,
    startRequest,
    updateCache,
  ]);

  const columns = useMemo(() => deriveKanbanColumnViews(cache), [cache]);
  const tasks = useMemo(
    () => TASK_STATUSES.flatMap((status) => columns[status].tasks),
    [columns],
  );
  const initialLoading = Boolean(projectId) && TASK_STATUSES.some(
    (status) => columns[status].initialLoading,
  );
  const initialized = !projectId || TASK_STATUSES.every(
    (status) => !columns[status].initialLoading,
  );

  return {
    dueDateSupported: dateSupport,
    tagFilterSupported: tagSupportProjectRef.current === projectId ? tagSupport : null,
    columns,
    tasks,
    initialLoading,
    initialized,
    taskLookupLoading,
    taskLookupError,
    loadMore,
    retry,
    revealPending,
    setColumnAtTop,
    refresh,
    upsert,
    moveTask,
    bulkAction,
    fetchTask,
  };
}
